import { useState, lazy, Suspense } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Button,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Tooltip,
} from '@heroui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'

import { SignInModal, Footer } from '../components'
import { GradientDefinitions } from '../features/shared/components'
import { useAuth, useRoles, useAdminView } from '../hooks'
import outlinedQuill from '../assets/outlined_quill.png'
import portraitGhost from '../assets/cyrano_profile.png'

// Lazy load the quick add modal
const QuickAddModal = lazy(() =>
  import('../features/gifs/components/QuickAddModal').then((module) => ({
    default: module.QuickAddModal,
  }))
)

export const AppLayout = () => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [isGifModalOpen, setIsGifModalOpen] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const isSearchActive = location.pathname === '/search'
  const { user, signOut: authSignOut, loading: authLoading } = useAuth()
  const { isAdmin, isActualAdmin, isAdminViewEnabled } = useRoles()
  const { toggleAdminView } = useAdminView()

  const handleSignIn = () => {
    setIsSignInModalOpen(true)
  }

  const handleSignOut = async () => {
    try {
      await authSignOut()
      navigate('/')
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  const handleAdminView = () => {
    navigate('/admin')
  }

  const handleOpenGifModal = () => {
    setIsGifModalOpen(true)
  }

  const handleGifModalSuccess = () => {
    // Modal will close automatically, no need to do anything else
    // The GIF components will automatically update via the real-time subscription
  }

  return (
    <div className="min-h-screen bg-gradient-custom-fixed flex flex-col">
      {/* Centralized SVG gradient definitions */}
      <GradientDefinitions />
      {/* Modern Navbar */}
      <nav className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto">
          <div className="flex h-16 items-center justify-between">
            {/* App Title */}
            <div className="flex items-center space-x-2">
              <Button
                variant="light"
                className="text-3xl font-bold text-foreground-500 hover:bg-transparent flex items-center gap-3"
                onPress={() => navigate('/')}
              >
                <img
                  src={outlinedQuill}
                  alt="Cyrano Quill Icon"
                  className="h-9"
                />
                Cyrano
              </Button>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-3">
              <Tooltip content="Search GIFs" placement="bottom">
                <Button
                  isIconOnly
                  variant="light"
                  aria-label="Search GIFs"
                  onPress={() => navigate(isSearchActive ? '/' : '/search')}
                  className={
                    isSearchActive ? 'text-primary' : 'text-foreground-500'
                  }
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </Button>
              </Tooltip>

              {/* Authentication Section */}
              {user ? (
                <Dropdown placement="bottom-end">
                  <DropdownTrigger>
                    <div className="relative">
                      <Avatar
                        as="button"
                        size="md"
                        src={user.photoURL || portraitGhost}
                        name={user.displayName || user.email || 'User'}
                        className="cursor-pointer hover:opacity-80 transition-opacity avatar-ai-gradient"
                      />
                      {isAdmin && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full border-2 border-background shadow-sm" />
                      )}
                    </div>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="User menu">
                    <DropdownSection showDivider>
                      <DropdownItem
                        key="profile"
                        className="h-14 gap-3"
                        textValue={`${user.displayName || user.email}`}
                        isReadOnly
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            size="sm"
                            src={user.photoURL || portraitGhost}
                            name={user.displayName || user.email || 'User'}
                            className="w-8 h-8 avatar-ai-gradient"
                          />
                          <div className="flex flex-col">
                            <p className="font-semibold text-foreground">
                              {user.displayName || user.email}
                            </p>
                            <p className="text-xs text-default-500">
                              {user.role} {user.role === 'admin' && '• Admin'}
                            </p>
                          </div>
                        </div>
                      </DropdownItem>
                    </DropdownSection>

                    {isActualAdmin ? (
                      <DropdownSection showDivider>
                        {isAdmin ? (
                          <>
                            <DropdownItem
                              key="quick-add"
                              onPress={handleOpenGifModal}
                              className="text-default-600"
                            >
                              Quick Add GIF
                            </DropdownItem>
                            <DropdownItem
                              key="admin"
                              onPress={handleAdminView}
                              className="text-default-600"
                            >
                              Admin Dashboard
                            </DropdownItem>
                          </>
                        ) : null}
                        <DropdownItem
                          key="toggle-admin-view"
                          onPress={toggleAdminView}
                          className="text-default-600"
                        >
                          {isAdminViewEnabled
                            ? '👤 Switch to User View'
                            : '⚡ Switch to Admin View'}
                        </DropdownItem>
                      </DropdownSection>
                    ) : null}

                    <DropdownSection>
                      <DropdownItem
                        key="logout"
                        color="danger"
                        onPress={handleSignOut}
                      >
                        Sign out
                      </DropdownItem>
                    </DropdownSection>
                  </DropdownMenu>
                </Dropdown>
              ) : (
                <Button
                  variant="light"
                  className="text-foreground hover:bg-default-100"
                  startContent={
                    <Avatar
                      size="sm"
                      className="w-6 h-6"
                      showFallback
                      name="U"
                    />
                  }
                  onPress={handleSignIn}
                  isLoading={authLoading}
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto p-4 flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <SignInModal
        isOpen={isSignInModalOpen}
        onOpenChange={setIsSignInModalOpen}
      />

      {/* Add GIF Modal */}
      <Suspense fallback={null}>
        <QuickAddModal
          isOpen={isGifModalOpen}
          onOpenChange={setIsGifModalOpen}
          onSuccess={handleGifModalSuccess}
        />
      </Suspense>
    </div>
  )
}
